import { Chart, ChartConfiguration, ChartDataset, TooltipItem } from 'chart.js';
import { NSpin, NTag } from 'naive-ui';
import { defineComponent, shallowRef } from 'vue';
import moment, { Moment } from 'moment';
import { mapState } from 'pinia';
import { useDashboardStore } from '~/store/dashboard.store';

import componentBase from '../component-base';
import SlotDetailsModal from '../slot-details-modal/slot-details-modal.vue';


import { config } from './chart.config';
import { AvoltaAlarmConfiguration } from '../../configs/alarms/avolta-alarm.config';
import { greenColor, greyLightColor } from '~/variables';
import { useStationStore } from '~/store/station.store';
import { DashboardSlots } from '~/api/models/dashboard/dashboard-history.model';

export enum DatasetEnum {
    OFFLINE = 'OFFLINE',
    ONLINE = 'ONLINE'
}

export default defineComponent({

    extends: componentBase,

    components: {
        SlotDetailsModal,

        NTag,
        NSpin,
    },

    data() {
        return {
            chart: null,
            datasets: [] as ChartDataset[],

            AvoltaAlarmConfiguration,
        }
    },

    computed: {
        ...mapState(useDashboardStore, ['since', 'before', 'history', 'loading']),
        ...mapState(useStationStore, ['stations']),
    },

    async mounted() {
        this.init();
    },

    methods: {

        init() {
            const chartElement: HTMLCanvasElement = this.$refs?.chart as HTMLCanvasElement;

            if (!chartElement) {
                return;
            }

            const configuration = {
                ...config,
            } as ChartConfiguration;

            configuration.options.scales.x.ticks.callback = (value, index) => {

                if(!this.history.length) {
                    return '';
                }

                const slot = this.history[index];

                const time = Math.floor((slot.end - slot.start) / 2 + slot.start);
                return moment.unix(time).format('ll');

            };

            configuration.options.onClick = (event, elements) => {
                if (elements[0] && this.history[elements[0].index]) {
                    //@ts-ignore
                    this.$refs?.modal.open(this.history[elements[0].index], 'connection')
                }
            }

            configuration.options.onHover = (event, elements) => {
                event.native.target.style.cursor = elements[0] ? 'pointer' : 'default';
            }

            configuration.options.plugins = {
                ...configuration.options.plugins,
                tooltip: {
                    callbacks: {
                        title: (items: TooltipItem<'bar'>[]) => {

                            if(!items?.length) {
                                return '';
                            }

                            const slot: DashboardSlots = this.history[items[0].dataIndex];

                            if(!slot) {
                                return items[0].label;
                            }

                            const day: Moment =  moment.unix(Math.floor((slot.end - slot.start) / 2 + slot.start));

                            const format = 'LL';

                            return day.format(format);
                        }
                    }
                }
            };

            //@ts-ignore
            this.chart = shallowRef(new Chart(chartElement.getContext('2d'), configuration));
        },

        compute(){

            if(!this.history) {
                return;
            }

            this.chart.data.labels = this.history.map(slot => {
                const time = Math.floor((slot.end - slot.start) / 2 + slot.start);
                return moment.unix(time).format('l')
            });    

            // @ts-ignore
            const datasets: { [key in DatasetEnum]: ChartDataset } = {};

            datasets[DatasetEnum.ONLINE] = ({ label: this.$t('global.online'), data: [], backgroundColor: greenColor });
            datasets[DatasetEnum.OFFLINE] = ({ label: this.$t('global.offline'), data: [], backgroundColor: greyLightColor });
         
            this.history.forEach(slot => {
                datasets[DatasetEnum.ONLINE].data.push(slot.online.length);
                datasets[DatasetEnum.OFFLINE].data.push(slot.offline?.length);
            });

            this.chart.data.datasets = Object.values(datasets);
        },

        updateChart() {

            if (!this.chart) {
                return;
            }

            try {
                this.compute();
                this.chart.options.scales.x.ticks.autoSkip = true;
                this.chart.update();
            } catch (e) {
                console.error(e);
                this.$notifyError(`An error occured while updating chart`, e);
            }
        },

    },

    watch: {
        loading(value, old) {
            if(!value && old) {
                this.updateChart();
            }
        }
    }
});
